import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { useCanWrite } from '../components/Can';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyScope } from '../hooks/useCompanyScope';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

// Machine Number Combo Box — type to filter or select, Tab/Enter accepts typed value
const MachineComboBox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  machines: { id: number; number: string }[];
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}> = ({ value, onChange, machines, onKeyDown }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 150 });
  const localRef = useRef<HTMLInputElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filtered = machines.filter(m =>
    String(m.number).includes(value)
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (localRef.current && !localRef.current.contains(e.target as Node) &&
          (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [isOpen]);

  const openDrop = () => {
    if (localRef.current) {
      const rect = localRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 3, left: rect.left, width: Math.max(rect.width, 150) });
    }
    setIsOpen(true);
  };

  return (
    <>
      <input
        ref={localRef}
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); if (!isOpen) openDrop(); }}
        onFocus={openDrop}
        onKeyDown={e => {
          if (e.key === 'Tab' || e.key === 'Enter') setIsOpen(false);
          onKeyDown?.(e);
        }}
        placeholder="Type machine no..."
        className="w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]"
      />
      {isOpen && filtered.length > 0 && createPortal(
        <div ref={dropdownRef}
          className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0_7px_29px_rgba(100,100,111,0.2)] max-h-40 overflow-y-auto py-0.5"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}>
          {filtered.map(m => (
            <button key={m.id} type="button" tabIndex={-1}
              onClick={() => { onChange(String(m.number)); setIsOpen(false); }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8F9FC] transition-colors ${value === String(m.number) ? 'bg-[#F8F9FC] text-[#0055a9] font-medium' : 'text-[#0E131B]'}`}>
              #{m.number}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const BeamMaster: React.FC = () => {
  const [mode, setMode] = useState<'list' | 'add'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Record<string, string>>({});
  const [actionMenuId, setActionMenuId] = useState<number | null>(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [deletingBeamId, setDeletingBeamId] = useState<number | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    beamNo: '', machineId: '', machineNo: '', taar: '', pano: '', taka: '',
    qualityDesiNo: '', colour: '', meter: '', beamPassedDate: '', beamMountedDate: '', beamRemovedDate: '',
    machineAttachedDate: new Date().toISOString().split('T')[0]
  });

  const queryClient = useQueryClient();

  const { user } = useAuth();
  const { showCompanyColumn } = useCompanyScope();
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);

  const { data: beamData, isLoading } = useQuery<any>({
    queryKey: ['beamMasters', searchQuery, page, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.set('search', searchQuery);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      return apiService.get(`/beam-masters?${params}`);
    }
  });

  const beamList = beamData?.beams || [];
  const pagination = beamData?.pagination || { total: 0, page: 1, pageSize, totalPages: 1 };

  const { data: nextBeamNo } = useQuery({
    queryKey: ['nextBeamNo'],
    queryFn: async () => {
      const data = await apiService.get('/beam-masters/next-beam-no');
      return data.nextBeamNo || 1;
    }
  });

  const { data: machinesList = [] } = useQuery<{ id: number; number: string }[]>({
    queryKey: ['machines-list'],
    queryFn: async () => {
      const data = await apiService.get('/machines');
      return (data.machines || data || []).map((m: any) => ({ id: m.id, number: String(m.number) }));
    }
  });

  const { data: takaQualities = [] } = useQuery<string[]>({
    queryKey: ['takaQualities'],
    queryFn: async () => {
      const data = await apiService.get('/taka-inventory/distinct-qualities');
      return data.qualities || [];
    }
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => apiService.post('/beam-masters', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beamMasters'] });
      queryClient.invalidateQueries({ queryKey: ['nextBeamNo'] });
      setMode('list');
      resetForm();
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to create')
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => apiService.put(`/beam-masters/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['beamMasters'] });
      setEditingId(null);
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to update')
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiService.delete(`/beam-masters/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['beamMasters'] }),
    onError: (error: any) => toast.error(error.response?.data?.message || 'Failed to delete')
  });

  const resetForm = () => {
    setFormData({
      beamNo: '', machineId: '', machineNo: '', taar: '', pano: '', taka: '',
      qualityDesiNo: '', colour: '', meter: '', beamPassedDate: '', beamMountedDate: '', beamRemovedDate: '',
      machineAttachedDate: new Date().toISOString().split('T')[0]
    });
    setSelectedTenantId(null); // reopening/after a successful save can't silently reuse the last company
  };

  const canWrite = useCanWrite('beam-master');
  const openAdd = () => {
    resetForm();
    setFormData(prev => ({ ...prev, beamNo: String(nextBeamNo || 1) }));
    setMode('add');
  };

  const handleScanBeamCard = async (file: File) => {
    setIsScanning(true);
    try {
      const formPayload = new FormData();
      formPayload.append('image', file);
      const token = localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:6002/api';
      const response = await fetch(`${apiBase}/ocr/scan-beam-card`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formPayload
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      const d = data.data;
      setFormData(prev => ({
        ...prev,
        beamNo: d.beamNo || prev.beamNo,
        machineNo: d.machineNo || prev.machineNo,
        taar: d.taar || prev.taar,
        pano: d.pano || prev.pano,
        taka: d.taka || prev.taka,
        qualityDesiNo: d.qualityDesiNo || prev.qualityDesiNo,
        meter: d.meter || prev.meter,
        beamPassedDate: d.beamPassedDate || prev.beamPassedDate,
        beamMountedDate: d.beamMountedDate || prev.beamMountedDate,
        beamRemovedDate: d.beamRemovedDate || prev.beamRemovedDate,
      }));
      // Try to match machine by number
      if (d.machineNo) {
        const machine = machinesList.find((m: { id: number; number: string }) => String(m.number) === String(d.machineNo));
        if (machine) {
          setFormData(prev => ({ ...prev, machineId: String(machine.id) }));
        }
      }
      toast.success('Beam card scanned successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to scan beam card');
    } finally {
      setIsScanning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = () => {
    if (!formData.beamNo) { toast.error('Beam No is required'); return; }
    if (showCompanyColumn && !selectedTenantId) { toast.error('Please select a company'); return; }
    const matchedMachine = machinesList.find((m: { id: number; number: string }) => String(m.number) === formData.machineNo.trim());
    createMutation.mutate({
      beamNo: parseInt(formData.beamNo),
      machineId: matchedMachine ? matchedMachine.id : null,
      machineNo: formData.machineNo || null,
      taar: formData.taar || null,
      pano: formData.pano || null,
      taka: formData.taka || null,
      qualityDesiNo: formData.qualityDesiNo || null,
      colour: formData.colour || null,
      meter: formData.meter ? parseFloat(formData.meter) : null,
      beamPassedDate: formData.beamPassedDate || null,
      beamMountedDate: formData.beamMountedDate || null,
      beamRemovedDate: formData.beamRemovedDate || null,
      machineAttachedDate: formData.machineAttachedDate || null,
      tenantId: showCompanyColumn ? selectedTenantId : undefined
    });
  };

  const openEdit = (b: any) => {
    setEditingId(b.id);
    setEditData({
      beamNo: String(b.beamNo || ''), machineNo: b.machineNo || '', taar: b.taar || '', pano: b.pano || '',
      taka: b.taka || '', qualityDesiNo: b.qualityDesiNo || '', colour: b.colour || '', meter: String(b.meter ?? ''),
      beamPassedDate: b.beamPassedDate || '', beamMountedDate: b.beamMountedDate || '',
      beamRemovedDate: b.beamRemovedDate || '', machineAttachedDate: b.machineAttachedDate || '', status: b.status || 'active'
    });
  };

  const handleEditSave = () => {
    if (!editingId) return;
    updateMutation.mutate({
      id: editingId,
      data: {
        beamNo: parseInt(editData.beamNo) || undefined,
        machineNo: editData.machineNo || null,
        taar: editData.taar || null, pano: editData.pano || null, taka: editData.taka || null,
        qualityDesiNo: editData.qualityDesiNo || null,
        colour: editData.colour || null,
        meter: editData.meter ? parseFloat(editData.meter) : null,
        beamPassedDate: editData.beamPassedDate || null,
        beamMountedDate: editData.beamMountedDate || null,
        beamRemovedDate: editData.beamRemovedDate || null,
        machineAttachedDate: editData.machineAttachedDate || null,
        status: editData.status || 'active'
      }
    });
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setActionMenuId(null);
      }
    };
    if (actionMenuId !== null) {
      document.addEventListener('mousedown', handler);
    }
    return () => document.removeEventListener('mousedown', handler);
  }, [actionMenuId]);

  const openActionMenu = (e: React.MouseEvent, id: number) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setActionMenuPos({ top: rect.bottom + 4, left: rect.right - 140 });
    setActionMenuId(prev => prev === id ? null : id);
  };

  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';
  const ic = "w-full px-2 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]";

  const handleFieldKeyDown = (e: React.KeyboardEvent, fieldIndex: number, totalFields: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (fieldIndex >= totalFields - 1) {
        handleSave();
      } else {
        const form = (e.target as HTMLElement).closest('[data-beam-form]');
        if (form) {
          const next = form.querySelector(`[data-field-index="${fieldIndex + 1}"]`) as HTMLElement;
          next?.focus();
        }
      }
    }
  };

  // LIST VIEW
  return (
    <>
    {/* Add/Edit Beam Modal */}
    {mode === 'add' && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center p-4" onClick={() => setMode('list')}>
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 10 }} transition={{ duration: 0.2, ease: 'easeOut' }} className="bg-white rounded-xl border border-[#E8EBF1] shadow-[0_7px_29px_rgba(100,100,111,0.2)] w-full max-w-5xl" onClick={e => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E8EBF1]">
            <h2 className="text-xl font-bold text-[#1B202A]">New Beam Entry</h2>
            <div className="flex items-center gap-2">
              <input type="file" ref={fileInputRef} accept="image/*" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleScanBeamCard(f); }} />
              <button onClick={() => fileInputRef.current?.click()} disabled={isScanning}
                className="inline-flex items-center gap-1.5 px-3 py-[7px] text-sm font-semibold text-[#0055a9] bg-[#F0F3FF] border border-[#D4DBEF] rounded-xl hover:bg-[#E4E9FC] disabled:opacity-50 transition-colors">
                {isScanning ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-[#0055a9] border-t-transparent"></div>
                    Scanning...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    Scan Card
                  </>
                )}
              </button>
              <button onClick={() => setMode('list')} className="p-1.5 text-[rgba(14,19,27,0.4)] hover:text-[#0E131B] hover:bg-[#F8F9FC] rounded-xl transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="px-6 py-6 space-y-5" data-beam-form>
            {showCompanyColumn && (
              <div className="max-w-xs">
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">COMPANY *</label>
                <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
              </div>
            )}
            {/* Row 1: Main fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">BEAM NO *</label>
                <input type="number" value={formData.beamNo} onChange={e => setFormData({ ...formData, beamNo: e.target.value })}
                  className={ic + " font-bold text-lg"} autoFocus data-field-index={0} onKeyDown={e => handleFieldKeyDown(e, 0, 10)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">MACHINE NO *</label>
                <MachineComboBox value={formData.machineNo} machines={machinesList}
                  onChange={(v) => setFormData(prev => ({ ...prev, machineNo: v }))}
                  onKeyDown={e => handleFieldKeyDown(e, 1, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">QUALITY</label>
                <input type="text" value={formData.qualityDesiNo} onChange={e => setFormData({ ...formData, qualityDesiNo: e.target.value })} className={ic} placeholder="Enter quality"
                  data-field-index={2} onKeyDown={e => handleFieldKeyDown(e, 2, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">COLOUR</label>
                <input type="text" value={formData.colour} onChange={e => setFormData({ ...formData, colour: e.target.value })} className={ic} placeholder="Enter colour"
                  data-field-index={3} onKeyDown={e => handleFieldKeyDown(e, 3, 11)} />
              </div>
            </div>

            {/* Row 2: Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">METER</label>
                <input type="number" step="0.01" value={formData.meter} onChange={e => setFormData({ ...formData, meter: e.target.value })} className={ic} placeholder="0.00"
                  data-field-index={4} onKeyDown={e => handleFieldKeyDown(e, 4, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">TAAR</label>
                <input type="text" value={formData.taar} onChange={e => setFormData({ ...formData, taar: e.target.value })} className={ic} placeholder="Wire count"
                  data-field-index={5} onKeyDown={e => handleFieldKeyDown(e, 5, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">PANO</label>
                <input type="text" value={formData.pano} onChange={e => setFormData({ ...formData, pano: e.target.value })} className={ic} placeholder="Reed count"
                  data-field-index={6} onKeyDown={e => handleFieldKeyDown(e, 6, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">TAKA</label>
                <input type="text" value={formData.taka} onChange={e => setFormData({ ...formData, taka: e.target.value })} className={ic} placeholder="Taka count"
                  data-field-index={7} onKeyDown={e => handleFieldKeyDown(e, 7, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">PASSED DATE</label>
                <input type="date" value={formData.beamPassedDate} onChange={e => setFormData({ ...formData, beamPassedDate: e.target.value })} className={ic}
                  data-field-index={8} onKeyDown={e => handleFieldKeyDown(e, 8, 11)} />
              </div>
            </div>

            {/* Row 3: Dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">MOUNTED DATE</label>
                <input type="date" value={formData.beamMountedDate} onChange={e => setFormData({ ...formData, beamMountedDate: e.target.value })} className={ic}
                  data-field-index={9} onKeyDown={e => handleFieldKeyDown(e, 9, 11)} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[rgba(14,19,27,0.65)] mb-1.5">REMOVED DATE</label>
                <input type="date" value={formData.beamRemovedDate} onChange={e => setFormData({ ...formData, beamRemovedDate: e.target.value })} className={ic}
                  data-field-index={10} onKeyDown={e => handleFieldKeyDown(e, 10, 11)} />
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#E8EBF1] bg-[#F8F9FC]/50 rounded-b-xl">
            <button onClick={() => setMode('list')}
              className="px-4 py-2 text-sm font-semibold text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F8F9FC] shadow-[1px_1px_3px_rgba(0,0,0,0.06)] transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={createMutation.isPending}
              className="px-5 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0d2780] disabled:opacity-50 transition-colors">
              {createMutation.isPending ? 'Saving...' : 'Save Beam'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
    <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="px-2 py-4">
      <div className="bg-white rounded-xl border border-[#E8EBF1]">
        {/* Header bar */}
        <div className="flex items-center px-5 py-4 border-b border-[#E8EBF1] gap-3 flex-wrap">
          <h1 className="text-2xl font-bold text-[#1B202A]">Beam Master</h1>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <div className="relative">
              <svg className="w-4 h-4 text-[rgba(14,19,27,0.4)] absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }} placeholder="Search beam..."
                className="pl-9 pr-3 py-2 text-sm bg-[#F8F9FC] border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] w-full sm:w-48" />
            </div>
            <button onClick={() => { openAdd(); setTimeout(() => fileInputRef.current?.click(), 100); }} disabled={isScanning || !canWrite}
              title={!canWrite ? 'You have read-only access' : undefined}
              className="inline-flex items-center gap-1.5 text-[#0055a9] bg-[#F0F3FF] border border-[#D4DBEF] rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#E4E9FC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {isScanning ? (
                <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-[#0055a9] border-t-transparent"></div> Scanning...</>
              ) : (
                <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg> Scan Card</>
              )}
            </button>
            <button onClick={openAdd}
              disabled={!canWrite}
              title={!canWrite ? 'You have read-only access' : undefined}
              className="inline-flex items-center gap-1.5 bg-[#0055a9] text-white rounded-xl px-4 py-2 text-sm font-semibold hover:bg-[#0B2278] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              New Beam
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="p-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#0055a9] border-t-transparent mx-auto mb-3"></div></div>
        ) : beamList.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="z-10 shadow-[0_1px_0_0_#E8EBF1]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Beam#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Machine</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Quality</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Colour</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Taar</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Pano</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Taka</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Total Mtr</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Remaining</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Passed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Mounted</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Removed</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Status</th>
                  {showCompanyColumn && (
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[rgba(14,19,27,0.65)] uppercase bg-[#F8F9FC]">Company</th>
                  )}
                  <th className="px-4 py-3 w-14 bg-[#F8F9FC]"></th>
                </tr>
              </thead>
              <tbody>
                {beamList.map((b: any, i: number) => {
                  const isEd = editingId === b.id;
                  const eic = "text-sm border border-[#E1E6EF] rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9] bg-white";
                  return (
                    <motion.tr key={b.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }} className={`border-b border-[#F1F3F9] transition-colors ${isEd ? 'bg-[#FFFBEB]' : 'hover:bg-[#FAFBFD]'}`}>
                      <td className="px-4 py-4 text-sm text-[#0E131B] font-semibold">{b.beamNo}</td>
                      <td className="px-4 py-4 text-sm text-[#0E131B]">
                        {isEd ? <input value={editData.machineNo} onChange={e => setEditData({ ...editData, machineNo: e.target.value })} className={eic + ' w-20'} />
                          : (b.machineNo || <span className="text-[rgba(14,19,27,0.3)]">-</span>)}
                      </td>
                      <td className="px-4 py-4 text-sm text-[#0E131B]">
                        {isEd ? <input value={editData.qualityDesiNo} onChange={e => setEditData({ ...editData, qualityDesiNo: e.target.value })} className={eic + ' w-20'} />
                          : b.qualityDesiNo ? <span className="text-sm">{b.qualityDesiNo}</span> : <span className="text-[rgba(14,19,27,0.3)]">-</span>}
                      </td>
                      <td className="px-4 py-4 text-sm text-[#0E131B]">
                        {isEd ? <input value={editData.colour} onChange={e => setEditData({ ...editData, colour: e.target.value })} className={eic + ' w-20'} />
                          : b.colour ? <span className="text-sm">{b.colour}</span> : <span className="text-[rgba(14,19,27,0.3)]">-</span>}
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{isEd ? <input value={editData.taar} onChange={e => setEditData({ ...editData, taar: e.target.value })} className={eic + ' w-14 text-center'} /> : (b.taar || <span className="text-[rgba(14,19,27,0.3)]">-</span>)}</td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{isEd ? <input value={editData.pano} onChange={e => setEditData({ ...editData, pano: e.target.value })} className={eic + ' w-14 text-center'} /> : (b.pano || <span className="text-[rgba(14,19,27,0.3)]">-</span>)}</td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{isEd ? <input value={editData.taka} onChange={e => setEditData({ ...editData, taka: e.target.value })} className={eic + ' w-14 text-center'} /> : (b.taka || <span className="text-[rgba(14,19,27,0.3)]">-</span>)}</td>
                      <td className="px-4 py-4 text-right text-sm text-[#0E131B] tabular-nums">
                        {isEd ? <input type="number" value={editData.meter} onChange={e => setEditData({ ...editData, meter: e.target.value })} className={eic + ' w-20 text-right'} /> :
                          (b.originalMeter != null ? parseFloat(b.originalMeter).toFixed(2) : b.meter != null ? parseFloat(b.meter).toFixed(2) : <span className="text-[rgba(14,19,27,0.3)]">-</span>)}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-semibold">
                        {b.meter != null ? (
                          <span className={`tabular-nums ${b.meter <= 0 ? 'text-red-600' : b.meter < 500 ? 'text-amber-600' : 'text-green-700'}`}>
                            {parseFloat(b.meter).toFixed(2)}
                            {b.meter <= 0 && <span className="ml-1 text-[9px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-bold">DONE</span>}
                            {b.meter > 0 && b.meter < 500 && <span className="ml-1 text-[9px] bg-amber-100 text-amber-600 px-1 py-0.5 rounded font-bold">LOW</span>}
                          </span>
                        ) : <span className="text-[rgba(14,19,27,0.3)]">-</span>}
                      </td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{isEd ? <input type="date" value={editData.beamPassedDate} onChange={e => setEditData({ ...editData, beamPassedDate: e.target.value })} className={eic + ' w-28'} /> : fmtDate(b.beamPassedDate)}</td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{isEd ? <input type="date" value={editData.beamMountedDate} onChange={e => setEditData({ ...editData, beamMountedDate: e.target.value })} className={eic + ' w-28'} /> : fmtDate(b.beamMountedDate)}</td>
                      <td className="px-4 py-4 text-center text-sm text-[#0E131B]">{isEd ? <input type="date" value={editData.beamRemovedDate} onChange={e => setEditData({ ...editData, beamRemovedDate: e.target.value })} className={eic + ' w-28'} /> : fmtDate(b.beamRemovedDate)}</td>
                      <td className="px-4 py-4 text-center">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${
                          b.status === 'active' || b.status === 'mounted' ? 'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]' :
                          b.status === 'removed' || b.status === 'completed' ? 'bg-[#FCEDB9] border-[#FCD579] text-[#A82C00]' :
                          'bg-[#D7F7C2] border-[#A6EB84] text-[#006908]'
                        }`}>{b.status}</span>
                      </td>
                      {showCompanyColumn && (
                        <td className="px-4 py-4 text-left text-sm text-[#0E131B]">
                          <CompanyCell tenantId={b.tenantId} />
                        </td>
                      )}
                      <td className="px-4 py-4 text-center">
                        {isEd ? (
                          <div className="inline-flex gap-1">
                            <button onClick={handleEditSave} className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg></button>
                            <button onClick={() => setEditingId(null)} className="p-1.5 rounded-lg text-[rgba(14,19,27,0.4)] hover:bg-[#F8F9FC] transition-colors"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                          </div>
                        ) : (
                          <button onClick={(e) => openActionMenu(e, b.id)} disabled={!canWrite} title={!canWrite ? 'You have read-only access' : undefined} className="p-1.5 rounded-lg text-[rgba(14,19,27,0.4)] hover:bg-[#F8F9FC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="4" r="1.5" /><circle cx="10" cy="10" r="1.5" /><circle cx="10" cy="16" r="1.5" /></svg>
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-16 text-center">
            <p className="text-[rgba(14,19,27,0.4)] font-medium mb-3">No beams found</p>
            <button onClick={openAdd} disabled={!canWrite} title={!canWrite ? 'You have read-only access' : undefined} className="px-4 py-2 text-sm font-semibold text-white bg-[#0055a9] rounded-xl hover:bg-[#0B2278] disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Add Beam</button>
          </div>
        )}

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E8EBF1] flex-wrap gap-2">
            <span className="text-sm text-[rgba(14,19,27,0.5)]">{(page - 1) * pageSize + 1}-{Math.min(page * pageSize, pagination.total)} of {pagination.total}</span>
            <div className="flex items-center">
              <div className="inline-flex rounded-xl border border-[#E1E6EF] overflow-hidden">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                  className="px-3 py-1.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-30 disabled:cursor-not-allowed border-r border-[#E1E6EF] transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                {Array.from({ length: Math.min(pagination.totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button key={pageNum} onClick={() => setPage(pageNum)}
                      className={`px-3 py-1.5 text-sm font-medium border-r border-[#E1E6EF] last:border-r-0 transition-colors ${
                        page === pageNum ? 'bg-[#0055a9] text-white' : 'text-[#0E131B] hover:bg-[#F8F9FC]'
                      }`}>
                      {pageNum}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                  className="px-3 py-1.5 text-sm text-[#0E131B] hover:bg-[#F8F9FC] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-[rgba(14,19,27,0.5)]">
              <span>Show</span>
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="px-2 py-1 text-sm bg-white border border-[#E1E6EF] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]">
                {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Action menu portal */}
      {actionMenuId !== null && createPortal(
        <div ref={actionMenuRef} className="fixed z-[9999] bg-white rounded-xl border border-[#E1E6EF] shadow-[0px_4px_16px_rgba(0,0,0,0.1)] py-1 w-36"
          style={{ top: actionMenuPos.top, left: actionMenuPos.left }}>
          <button
            onClick={() => { const b = beamList.find((b: any) => b.id === actionMenuId); if (b) openEdit(b); setActionMenuId(null); }}
            className="w-full text-left px-4 py-2 text-sm text-[#0E131B] hover:bg-[#F8F9FC] transition-colors">
            Edit
          </button>
          <button
            onClick={() => { setDeletingBeamId(actionMenuId); setActionMenuId(null); }}
            className="w-full text-left px-4 py-2 text-sm text-[#E02D3C] hover:bg-[#FEF2F2] transition-colors">
            Delete
          </button>
        </div>,
        document.body
      )}
      {/* Delete Confirmation Modal */}
      {deletingBeamId && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-4"
          tabIndex={-1} ref={el => el?.focus()}
          onKeyDown={e => { if (e.key === 'Escape') setDeletingBeamId(null); if (e.key === 'Enter') { e.preventDefault(); deleteMutation.mutate(deletingBeamId); setDeletingBeamId(null); } }}>
          <div className="bg-white rounded-xl shadow-lg w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-[#1B202A] mb-2">Delete Beam</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to delete this beam entry? This action cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingBeamId(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { deleteMutation.mutate(deletingBeamId); setDeletingBeamId(null); }}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
    </>
  );
};

export default BeamMaster;
